import styled from "@emotion/styled";

export const MapWrapper = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

export const MapContainer = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: white;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

export const Placeholder = styled.div`
  color: #9ca3af;
  font-style: italic;
  padding: 0.5rem;
  font-size: 0.8125rem;
  text-align: center;
  background: #f9fafb;
  border-radius: 6px;
  margin-top: 0.5rem;
`;

export const ErrorText = styled.div`
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.375rem;
  font-weight: 500;
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.96);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 8px;

  .spinner {
    width: 32px;
    height: 32px;
    border: 2px solid #f3f4f6;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 0.75rem;

    &.small {
      width: 20px;
      height: 20px;
      border-width: 2px;
      margin-bottom: 0.5rem;
    }
  }

  p {
    color: #6b7280;
    font-size: 0.8125rem;
    font-weight: 500;
    margin: 0;
  }

  span {
    color: #9ca3af;
    font-size: 0.75rem;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const InstructionsPanel = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.8125rem;
  color: #6b7280;

  strong {
    color: #374151;
    font-size: 0.875rem;
  }
`;

export const ToggleButton = styled.button<{ isOpen?: boolean }>`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  color: #6b7280;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    box-shadow: 0 1px 4px rgba(59, 130, 246, 0.15);
  }

  ${(props) =>
    props.isOpen &&
    `
    border-color: #3b82f6;
    color: #3b82f6;
  `}
`;

export const ControlPanel = styled.div`
  position: absolute;
  top: 3rem;
  left: 0.75rem;
  background: white;
  border-radius: 6px;
  padding: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  min-width: 180px;
  max-width: 220px;
  z-index: 10;
  animation: slideDown 0.15s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const StatsPanel = styled.div`
  display: flex;
  gap: 0.375rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
`;

export const PolygonBadge = styled.span`
  background: #3b82f6;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const CurrentPathInfo = styled.div`
  border-top: 1px solid #f3f4f6;
  padding-top: 0.625rem;
  margin-bottom: 0.625rem;

  span {
    display: block;
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  div {
    display: flex;
    gap: 0.375rem;
  }
`;

interface ButtonProps {
  variant?: "primary" | "outline" | "danger";
  size?: "small" | "medium";
}

export const Button = styled.button<ButtonProps>`
  padding: ${(props) =>
    props.size === "small" ? "0.375rem 0.625rem" : "0.5rem 0.75rem"};
  border: 1px solid;
  border-radius: 6px;
  font-size: ${(props) => (props.size === "small" ? "0.75rem" : "0.8125rem")};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  ${(props) => {
    switch (props.variant) {
      case "outline":
        return `
          background: white;
          border-color: #e5e7eb;
          color: #6b7280;
          &:hover:not(:disabled) {
            background: #f9fafb;
            border-color: #3b82f6;
            color: #3b82f6;
          }
        `;
      case "danger":
        return `
          background: #ef4444;
          border-color: #ef4444;
          color: white;
          &:hover:not(:disabled) {
            background: #dc2626;
            border-color: #dc2626;
          }
        `;
      default:
        return `
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
          &:hover:not(:disabled) {
            background: #2563eb;
            border-color: #2563eb;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }
`;

// Search Components
export const SearchContainer = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 16rem;
  right: 0.75rem;
  max-width: 360px;
  z-index: 10;
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.75rem 0.625rem 2.25rem;
  border: none;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #374151;
  background: transparent;
  outline: none;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

export const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
`;

export const ClearButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;

  &:hover {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

export const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 180px;
  overflow-y: auto;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  z-index: 100;
`;

export const SearchResultItem = styled.div`
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.1s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }

  &:active {
    background: #f3f4f6;
  }
`;
